import React from "react";
import { relativeTime, initialsFor } from "../HomePage";
import type { RecentFile } from "../shared/recents";
import type { DraftSnapshot } from "../shared/draft";

/* ============================================================
   src/mobile/MobileHomeTab.tsx
   ============================================================
   The phone's Home tab. Same idea as the desktop Home screen,
   but built from what a phone actually has, which is not the
   same thing:

   • The estimate in progress lives in the cloud draft, not in a
     local file, so the Continue card is filled from there and
     renders a skeleton until that resolves. Reserving the space
     matters more here than on desktop — a card appearing late on
     a 390px screen shoves everything below it.
   • Recents only exist on this device once a .est has been opened
     out of Files/Dropbox, so the section is omitted entirely when
     empty rather than showing an empty-state box. Screen space on
     a phone is too expensive to spend explaining an absence.
   • No "Jump to" grid: the only other destination is Settings,
     and that is already a tab.

   relativeTime / initialsFor are imported from the desktop Home
   rather than reimplemented — same list, same labels, one set of
   tests covering both.
   ============================================================ */

export type MobileDraftSummary = {
  clientName: string;
  town: string;
  savedAt: number | null;
};

export type MobileHomeTabProps = {
  userName?: string | null;
  companyName?: string | null;
  /** Summary of the cloud draft; null once we know there isn't a usable one. */
  draft: MobileDraftSummary | null;
  /** True while the cloud draft is still being fetched. */
  draftLoading: boolean;
  recents: RecentFile[];
  onContinue: () => void;
  onNewEstimate: () => void;
  onOpenFile: () => void;
  onOpenRecent: (rf: RecentFile) => void;
};

/**
 * Reduce a cloud draft to what the Continue card shows, or null when there is
 * nothing worth going back to.
 *
 * A draft row exists for every user who has ever opened the estimator, so
 * "a row came back" is NOT the same as "there is work in progress" — without
 * this check the card would offer to return you to a blank estimate on a fresh
 * install. Requires either a named customer or some actual scope.
 */
export function summarizeDraft(
  snap: DraftSnapshot | null
): MobileDraftSummary | null {
  if (!snap) return null;
  const first = (snap.clientFirstName || "").trim();
  const last = (snap.clientLastName || "").trim();
  const town = (snap.clientTown || "").trim();
  const hasScope =
    !!(snap.constructionType || "").trim() ||
    !!(snap.selectedDeckingId || "").trim() ||
    Number(snap.deckingSqFt || 0) > 0 ||
    !!(snap.selectedRailingId || "").trim() ||
    Number(snap.railingLf || 0) > 0 ||
    (Array.isArray(snap.addItems) && snap.addItems.length > 0);
  if (!first && !last && !town && !hasScope) return null;
  const savedAt = snap.savedAt ? new Date(snap.savedAt).getTime() : NaN;
  return {
    clientName: [first, last].filter(Boolean).join(" "),
    town,
    savedAt: Number.isFinite(savedAt) ? savedAt : null,
  };
}

function greetingFor(date: Date): string {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function MobileHomeTab({
  userName,
  companyName,
  draft,
  draftLoading,
  recents,
  onContinue,
  onNewEstimate,
  onOpenFile,
  onOpenRecent,
}: MobileHomeTabProps) {
  const now = React.useMemo(() => new Date(), []);
  const firstName = String(userName || "").trim().split(/\s+/)[0] || "";
  const dateLabel = now.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const shownRecents = (recents || []).slice(0, 6);

  return (
    <main className="m-content m-home">
      <header className="m-home-hero">
        <h1 className="m-home-greeting">
          {greetingFor(now)}
          {firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="m-home-sub">
          {companyName ? `${companyName} · ` : ""}
          {dateLabel}
        </p>
      </header>

      {draftLoading ? (
        <div className="m-home-card m-home-card--skeleton" aria-hidden>
          <span className="m-home-skel m-home-skel--eyebrow" />
          <span className="m-home-skel m-home-skel--title" />
          <span className="m-home-skel m-home-skel--sub" />
        </div>
      ) : draft ? (
        <button
          type="button"
          className="m-home-card m-home-card--resume"
          onClick={onContinue}
        >
          {/* Desktop says "Pick up where you left off"; at 390px with this
              tracking that phrase runs off the card. */}
          <span className="m-home-eyebrow">In progress</span>
          <span className="m-home-title">
            {draft.clientName || "Untitled estimate"}
          </span>
          <span className="m-home-sub-line">
            {[
              draft.town,
              draft.savedAt
                ? `saved ${relativeTime(draft.savedAt, now.getTime())}`
                : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          <span className="m-home-cta">Continue →</span>
        </button>
      ) : null}

      <div className="m-home-row">
        <button type="button" className="m-home-action" onClick={onNewEstimate}>
          <span className="m-home-action-glyph" aria-hidden>
            +
          </span>
          <span className="m-home-action-label">New estimate</span>
        </button>
        <button type="button" className="m-home-action" onClick={onOpenFile}>
          <span className="m-home-action-glyph" aria-hidden>
            ↥
          </span>
          <span className="m-home-action-label">Open a file</span>
        </button>
      </div>

      {shownRecents.length > 0 && (
        <section className="m-home-section">
          <h2 className="m-home-section-title">Recent estimates</h2>
          <div className="m-home-recents">
            {shownRecents.map((rf) => {
              const snap = rf?.json || {};
              const named = [
                String(snap.clientFirstName || "").trim(),
                String(snap.clientLastName || "").trim(),
              ]
                .filter(Boolean)
                .join(" ");
              const title = named || String(rf.name || "").trim() || "Estimate";
              const town = String(snap.clientTown || "").trim();
              return (
                <button
                  key={`${rf.ts}-${rf.name}`}
                  type="button"
                  className="m-home-recent"
                  onClick={() => onOpenRecent(rf)}
                >
                  <span className="m-home-mono" aria-hidden>
                    {initialsFor(title)}
                  </span>
                  <span className="m-home-recent-body">
                    <span className="m-home-recent-title">{title}</span>
                    {town && <span className="m-home-recent-sub">{town}</span>}
                  </span>
                  <span className="m-home-recent-time">
                    {relativeTime(rf.ts, now.getTime())}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}
